import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Package, Star, Gift, Settings, LogOut } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function AccountPage() {
  const { user, axiosInstance, updateProfile, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [loyalty, setLoyalty] = useState({ points: 0, transactions: [] });
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    address: user?.address || '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, loyaltyRes] = await Promise.all([
        axiosInstance.get('/orders'),
        axiosInstance.get('/loyalty'),
      ]);
      setOrders(ordersRes.data);
      setLoyalty(loyaltyRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateProfile(profileData);
      toast.success('Profil mis à jour !');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const tabs = [
    { id: 'orders', label: 'Commandes', icon: <Package size={20} /> },
    { id: 'loyalty', label: 'Fidélité', icon: <Gift size={20} /> },
    { id: 'settings', label: 'Paramètres', icon: <Settings size={20} /> },
  ];

  const orderStatusLabels = {
    pending: { label: 'En attente', color: 'bg-ocre/20 text-ocre' },
    confirmed: { label: 'Confirmée', color: 'bg-sage/20 text-sage-600' },
    preparing: { label: 'En préparation', color: 'bg-blue-100 text-blue-600' },
    delivering: { label: 'En livraison', color: 'bg-purple-100 text-purple-600' },
    delivered: { label: 'Livrée', color: 'bg-sage/20 text-sage-600' },
    cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-600' },
  };

  return (
    <main className="min-h-screen bg-transparent pt-24 pb-16">
      <div className="container-padding max-w-5xl mx-auto py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 shadow-card mb-8"
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-terracotta/20 rounded-full flex items-center justify-center">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <User className="text-terracotta" size={32} />
              )}
            </div>
            <div className="flex-1">
              <h1 className="font-heading text-2xl font-bold text-navy">
                {user?.name}
              </h1>
              <p className="text-navy/60">{user?.email}</p>
            </div>
            <div className="text-right hidden sm:block">
              <p className="text-sm text-navy/60">Points de fidélité</p>
              <p className="text-2xl font-bold text-terracotta">{loyalty.points}</p>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-terracotta text-white'
                  : 'bg-white text-navy hover:bg-terracotta/10'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="space-y-4">
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <div key={i} className="bg-white/50 h-32 rounded-2xl animate-pulse" />
                ))
              ) : orders.length > 0 ? (
                orders.map((order) => (
                  <div
                    key={order.order_id}
                    className="bg-white rounded-2xl p-5 shadow-card"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div>
                        <p className="text-sm text-navy/60">
                          Commande #{order.order_id.slice(-8)}
                        </p>
                        <p className="font-semibold text-navy">
                          {format(new Date(order.created_at), 'PPP', { locale: fr })}
                        </p>
                      </div>
                      <div className="text-right">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            orderStatusLabels[order.order_status]?.color
                          }`}
                        >
                          {orderStatusLabels[order.order_status]?.label}
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      {order.items.slice(0, 2).map((item, i) => (
                        <div key={`${order.order_id}-${item.dish_id}-${i}`} className="flex justify-between text-sm">
                          <span className="text-navy/70">
                            {item.quantity}x {item.name}
                          </span>
                          <span className="text-navy">
                            {item.item_total?.toFixed(2)} CHF
                          </span>
                        </div>
                      ))}
                      {order.items.length > 2 && (
                        <p className="text-sm text-navy/50">
                          + {order.items.length - 2} autres articles
                        </p>
                      )}
                    </div>
                    
                    <div className="flex justify-between items-center pt-4 border-t border-stone-200">
                      <div className="text-sm text-navy/60">
                        Livraison : {order.delivery_slot?.date} à {order.delivery_slot?.time_slot}
                      </div>
                      <div className="font-bold text-terracotta">
                        {order.total_amount?.toFixed(2)} CHF
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-2xl p-8 text-center">
                  <Package className="mx-auto text-navy/30 mb-4" size={48} />
                  <p className="text-navy/60">Aucune commande pour le moment</p>
                </div>
              )}
            </div>
          )}

          {/* Loyalty Tab */}
          {activeTab === 'loyalty' && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-card">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading text-xl font-semibold text-navy">
                      Programme de fidélité
                    </h2>
                    <p className="text-sm text-navy/60">
                      Gagnez 1 point par CHF dépensé
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-terracotta">{loyalty.points}</p>
                    <p className="text-sm text-navy/60">points</p>
                  </div>
                </div>

                <div className="bg-sand rounded-xl p-4 mb-4">
                  <p className="text-sm text-navy/70">
                    <strong className="text-navy">100 points = 5 CHF de réduction</strong>
                    <br />
                    Utilisez vos points lors de votre prochaine commande.
                  </p>
                </div>

                {loyalty.points >= 100 && (
                  <button
                    className="btn-primary w-full"
                    data-testid="redeem-points-button"
                  >
                    Utiliser mes points
                  </button>
                )}
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-card">
                <h3 className="font-heading text-lg font-semibold text-navy mb-4">
                  Historique des points
                </h3>
                {loyalty.transactions.length > 0 ? (
                  <div className="space-y-3">
                    {loyalty.transactions.map((txn) => (
                      <div
                        key={txn.transaction_id}
                        className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0"
                      >
                        <div>
                          <p className="text-sm font-medium text-navy">
                            {txn.description}
                          </p>
                          <p className="text-xs text-navy/50">
                            {format(new Date(txn.created_at), 'PPp', { locale: fr })}
                          </p>
                        </div>
                        <span
                          className={`font-bold ${
                            txn.type === 'earned' ? 'text-sage' : 'text-terracotta'
                          }`}
                        >
                          {txn.type === 'earned' ? '+' : ''}{txn.points}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-navy/60 text-center py-4">
                    Aucune transaction pour le moment
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="bg-white rounded-2xl p-6 shadow-card">
              <h2 className="font-heading text-xl font-semibold text-navy mb-6">
                Paramètres du compte
              </h2>
              
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    Nom complet
                  </label>
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) =>
                      setProfileData({ ...profileData, name: e.target.value })
                    }
                    className="input-field w-full"
                    data-testid="settings-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) =>
                      setProfileData({ ...profileData, phone: e.target.value })
                    }
                    className="input-field w-full"
                    data-testid="settings-phone-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-navy mb-2">
                    Adresse de livraison par défaut
                  </label>
                  <textarea
                    value={profileData.address}
                    onChange={(e) =>
                      setProfileData({ ...profileData, address: e.target.value })
                    }
                    rows={3}
                    className="input-field w-full resize-none"
                    data-testid="settings-address-input"
                  />
                </div>
                
                <button
                  type="submit"
                  className="btn-primary"
                  data-testid="save-settings-button"
                >
                  Enregistrer les modifications
                </button>
              </form>

              <hr className="border-stone-200 my-6" />

              <button
                onClick={logout}
                className="flex items-center gap-2 text-red-500 hover:text-red-600 font-medium"
                data-testid="logout-settings-button"
              >
                <LogOut size={20} />
                Se déconnecter
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </main>
  );
}
